import React from 'react';
import PropTypes from 'prop-types';
import { ListGroup, ListGroupItem, Panel } from 'react-bootstrap';

import Utils from '../util/Utils.jsx';
import { useStateValue } from '../context/state.jsx';
import { openAlert, openPrompt } from '../context/alert.js';
import { buildWorkbookModel } from '../util/ThreeWeekLookAhead.js';
import { filterWBSData, filterActivityCodeTree } from '../util/FilterLogic.jsx';

const ExportList = ({
	currentDataset
}) => {
	const [{
		emailAddress,
		datasets,
		filteredActivities,
		filteredActivitiesMap,
		projectFilter,
		wbsData,
		projectActivityCodesTree,
		projectViewType,
		projectGroupBy,
		projectGroupByActivityCode
	}, dispatch] = useStateValue();

	const doExport = (type, size) => {
		dispatch(openPrompt('What is your email address?', email => {
			Utils.ajax(
				'export',
				{ dataset: currentDataset, type, size, email },
				dispatch,
				result => {
					dispatch(openAlert('Exported', 'Successfully exported. Please check your email.'));
				}
			);
		},
		emailAddress,
		'Email Address'));
	};

	const saveBlob = (blob, filename) => {
		const objectUrl = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = objectUrl;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(objectUrl);
	};

	// Fetch rather than navigate, so a server error becomes an alert instead of
	// a downloaded file full of JSON.
	const doScheduleCsvDownload = async size => {
		const params = new URLSearchParams({
			dataset: String(currentDataset),
			token: localStorage.getItem('token') || '',
			size
		});
		const url = `${Utils.getApiOrigin()}/server/exportScheduleCsv.php?${params.toString()}`;
		try {
			const response = await fetch(url, { credentials: 'same-origin' });
			const contentType = response.headers.get('Content-Type') || '';
			if (!response.ok || contentType.indexOf('csv') === -1) {
				const body = await response.text();
				let message = 'The schedule CSV could not be generated.';
				try {
					const parsed = JSON.parse(body);
					if (parsed && parsed.error) {
						message = parsed.error;
					}
				} catch (parseError) {
					if (body.trim()) {
						message = body.trim().slice(0, 300);
					}
				}
				dispatch(openAlert('Export failed', message));
				return;
			}
			const disposition = response.headers.get('Content-Disposition') || '';
			const match = disposition.match(/filename="?([^";]+)"?/i);
			saveBlob(await response.blob(), match ? match[1] : `schedule-${size}.csv`);
		} catch (error) {
			dispatch(openAlert('Export failed', 'Could not reach the server to download the schedule CSV.'));
		}
	};

	const datasetMeta = datasets && currentDataset && datasets[currentDataset]
		? datasets[currentDataset]
		: null;

	const doThreeWeekLookAheadDownload = async () => {
		try {
			// exceljs is large; load the writer only when the user exports.
			const { writeLookAheadBuffer, lookAheadFilename } = await import('../util/ThreeWeekLookAheadExcel.js');
			const filteredWbs = wbsData && filteredActivitiesMap
				? filterWBSData(wbsData, filteredActivitiesMap)
				: wbsData;
			const filteredCodes = projectActivityCodesTree && filteredActivitiesMap
				? filterActivityCodeTree(projectActivityCodesTree, filteredActivitiesMap)
				: projectActivityCodesTree;
			const model = buildWorkbookModel({
				dataset: datasetMeta,
				filteredActivities: filteredActivities || [],
				projectFilter,
				wbsData: filteredWbs,
				projectActivityCodesTree: filteredCodes,
				projectViewType,
				projectGroupBy,
				projectGroupByActivityCode
			});
			const { buffer } = await writeLookAheadBuffer({ model });
			saveBlob(
				new Blob([buffer], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
				}),
				lookAheadFilename(datasetMeta && datasetMeta.Name)
			);
		} catch (error) {
			console.error('3WLA export failed', error);
			dispatch(openAlert(
				'Export failed',
				'The 3-Week Look Ahead workbook could not be generated.'
			));
		}
	};

	const sourceFormat = datasetMeta ? datasetMeta.SourceFormat : 'xer';
	const hasExportSkeleton = !!(datasetMeta && datasetMeta.hasExportSkeleton);
	const canXerExport = !!(datasetMeta && (datasetMeta.Owned || (datasetMeta.Permissions && '5' in datasetMeta.Permissions)));
	const canScheduleCsv = !!(datasetMeta && (datasetMeta.Owned || (datasetMeta.Permissions && '8' in datasetMeta.Permissions)));
	const types = [];
	if (canScheduleCsv) {
		types.push({
			type: '3wla',
			size: 'xlsx',
			text: '3-Week Look Ahead (.xlsx)',
			lookAhead: true
		});
		types.push({
			type: 'csv',
			size: 'full',
			text: 'Full schedule CSV',
			download: true
		});
		types.push({
			type: 'csv',
			size: 'incremental',
			text: 'Incremental schedule CSV',
			download: true
		});
	}
	if (sourceFormat === 'mspdi' && canXerExport) {
		types.push({
			type: 'csv',
			size: 'status',
			text: 'Status CSV'
		});
	}
	if (canXerExport && (sourceFormat !== 'mspdi' || hasExportSkeleton)) {
		types.push({
			type: 'xer',
			size: 'incremental',
			text: 'Incremental XER'
		});
	}
	if (canXerExport && sourceFormat !== 'mspdi') {
		types.push({
			type: 'xer',
			size: 'full',
			text: 'Full XER'
		});
	}
	const listGroupItems = types.map((item, index) => {
		return (
			<ListGroupItem
				key={index}
				className="exportListItem"
				onClick={() => {
					if (item.lookAhead) {
						doThreeWeekLookAheadDownload();
					} else if (item.download) {
						doScheduleCsvDownload(item.size);
					} else {
						doExport(item.type, item.size);
					}
				}}>
				{item.text}
				<span className="glyphicon glyphicon-chevron-right rightGlyph"></span>
			</ListGroupItem>
		);
	});
	return (
		<Panel defaultExpanded header="Export">
			<Panel.Heading>
				<Panel.Title toggle>Export</Panel.Title>
			</Panel.Heading>
			<Panel.Collapse>
				<ListGroup>
					{listGroupItems}
				</ListGroup>
			</Panel.Collapse>
		</Panel>
	);
};

ExportList.propTypes = {
	currentDataset: PropTypes.string
};

export default ExportList;